import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom/cjs/react-router-dom';
import {
  Confirmation,
  CreateButton,
  Currency,
  DateTime,
  Dropdown,
  FiltersBar,
  IconButton,
  ListView,
  ListViewActions,
  ListViewMenu,
  Page,
  Percent,
  ProjectLink,
  SearchInput,
} from '~/components';
import FixedDropdown from '~/components/FixedDropdown';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort } from '~/utils';
import OpportunityToProjectForm from '../OpportunityToProjectForm';
import OpportunityProjectsBoxes from './OpportunityProjectsBoxes';
import OpportunityProjectsForm from './OpportunityProjectsForm';
import ProjectSelect from './ProjectSelect';
import ViewOpportunityProject from './ViewOpportunityProject';

export default function OpportunityProjectsTab({ opportunity, onChange }) {
  const documentTitle = useDocumentTitle();

  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const confirmation = useConfirmation();
  const { workspace } = useWorkspace();
  const { projectId, action } = useParams();

  const [query, setQuery] = useState({ status: 'loading', data: null });
  const [params, setParams] = useState({
    q: '',
    sort: new QuerySort('project.name', 'asc'),
  });
  const [stats, setStats] = useState({});
  const [opportunityToProject, setOpportunityToProject] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .opportunities(opportunity.id)
        .projects()
        .get({ ...params });

      setQuery({ status: 'ready', data, error: null });
    } catch (error) {
      setQuery({ status: 'ready', data: null, error });
    }
  }, [api, workspace.id, opportunity, params]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchStats = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).opportunities(opportunity.id).projects().stats();
      setStats(data);
    } catch (error) {
      setStats({});
    }
  }, [api, workspace.id, opportunity.id]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  async function handleSaved() {
    fetchData();
    fetchStats();
    onChange();
  }

  async function handleDeleted() {
    handleSaved();
    handleClose();
  }

  async function handleCreated() {
    handleSaved();
    handleClose();
    setOpportunityToProject(false);
  }

  const handleChange = ({ target: { name, value } }) => {
    setQuery((state) => ({ ...state, status: 'filtering' }));
    setParams((params) => ({ ...params, [name]: value }));
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    setQuery((state) => ({ ...state, status: 'filtering' }));
    setParams((state) => ({ ...state, sort: new QuerySort(column, direction) }));
  };

  async function handleAddProject(project) {
    try {
      await api.www.workspaces(workspace.id).opportunities(opportunity.id).projects(project.id).add();
      await handleSaved();
    } catch (error) {
      toast.error(error.message || 'There was a problem associating the project. Please try again.');
    }
  }

  function handleClose() {
    history.push(`/app/${workspace.key}/pipeline/opportunities/${opportunity.id}/projects`);
    documentTitle.set(opportunity.name);
  }

  const handleDeleteConfirmation = async (projectId) => {
    const confirm = await confirmation.prompt((resolve) => (
      <Confirmation resolve={resolve}>Are you sure you want to remove the association with this project?</Confirmation>
    ));

    if (!confirm) return;
    await api.www.workspaces(workspace.id).opportunities(opportunity.id).projects(projectId).remove();

    await handleDeleted();
  };

  const handleView = ({ id }) => {
    history.push(`/app/${workspace.key}/pipeline/opportunities/${opportunity.id}/projects/${id}/view`);
  };

  const handleEdit = ({ id }) => {
    history.push(`/app/${workspace.key}/pipeline/opportunities/${opportunity.id}/projects/${id}/edit`);
  };

  if (query.status === 'loading') return <PageLoader />;

  return (
    <>
      <Page.TabHeader>
        <Page.Title>Projects</Page.Title>

        <Page.Actions>
          <FixedDropdown align="right">
            {({ setIsOpen, isOpen }) => (
              <>
                <FixedDropdown.Trigger>
                  <IconButton
                    tooltip="Associate Existing Project"
                    icon="link"
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={!opportunity.permissions.manage}
                  />
                </FixedDropdown.Trigger>
                <FixedDropdown.Menu>
                  <FixedDropdown.Panel style={{ width: '20rem' }}>
                    <ProjectSelect
                      filterProjects={query.data}
                      opportunity={opportunity}
                      onChange={async ({ target: { value } }) => {
                        handleAddProject(value);
                        setIsOpen(false);
                      }}
                    />
                  </FixedDropdown.Panel>
                </FixedDropdown.Menu>
              </>
            )}
          </FixedDropdown>
          {opportunity.permissions.manage && (
            <CreateButton tooltip="New Project" onClick={() => setOpportunityToProject(opportunity)} />
          )}
        </Page.Actions>
      </Page.TabHeader>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.Section sticky>
        <OpportunityProjectsBoxes
          opportunity={opportunity}
          availableAmount={stats.availableAmount}
          projectCount={stats.projectCount}
        />
      </Page.Section>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column minWidth="16rem" name="project.name" onSort={handleSort} sort={params.sort}>
              Name
            </ListView.Column>
            <ListView.Column
              width="16rem"
              name="opportunityProjects.amount"
              align="right"
              onSort={handleSort}
              sort={params.sort}>
              Amount
            </ListView.Column>
            <ListView.Column width="16rem" align="right">
              Percentage of Total
            </ListView.Column>
            <ListView.Column
              width="8rem"
              name="opportunityProjects.createdAt"
              onSort={handleSort}
              sort={params.sort}
              align="right">
              Created
            </ListView.Column>

            <ListViewActions.Column />
          </ListView.Header>

          <ListView.Body fade={query.status === 'filtering'}>
            {query.data?.map((project) => {
              const { id, createdAt, amount, currency } = project;

              async function handleRowClick(project) {
                if (opportunity.permissions.manage && project.permissions.edit) {
                  handleEdit(project);
                } else {
                  handleView(project);
                }
              }

              return (
                <ListView.Row key={id} onClick={() => handleRowClick(project)}>
                  <ListView.Cell>
                    <ProjectLink project={project} onClick={(e) => e.stopPropagation()} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <Currency value={amount} currency={currency} minimumFractionDigits={0} maximumFractionDigits={0} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <Percent
                      value={opportunity.amount ? amount / opportunity.amount : null}
                      minimumFractionDigits={0}
                      maximumFractionDigits={0}
                    />
                  </ListView.Cell>
                  <ListView.Cell>
                    <DateTime value={createdAt} />
                  </ListView.Cell>

                  <ListViewActions>
                    {opportunity.permissions.manage && project.permissions.edit ? (
                      <ListViewActions.Edit onClick={() => handleEdit(project)} />
                    ) : (
                      <ListViewActions.View onClick={() => handleView(project)} />
                    )}

                    <hr />

                    <ListViewMenu>
                      {({ setIsOpen }) => {
                        const handleAction = (action) => setIsOpen(false) || action;

                        return (
                          <>
                            <Dropdown.Item onClick={() => handleAction(handleView(project))}>View</Dropdown.Item>

                            <Dropdown.Item
                              disabled={!opportunity.permissions.manage || !project.permissions.edit}
                              tooltip={
                                !opportunity.permissions.manage || !project.permissions.edit
                                  ? 'Insufficient permissions to edit this project.'
                                  : undefined
                              }
                              onClick={() => handleAction(handleEdit(project))}>
                              Edit
                            </Dropdown.Item>

                            <Dropdown.Item
                              disabled={!opportunity.permissions.manage || !project.permissions.edit}
                              tooltip={
                                !opportunity.permissions.manage || !project.permissions.edit
                                  ? 'Insufficient permissions to remove this project.'
                                  : undefined
                              }
                              onClick={() => handleAction(handleDeleteConfirmation(project.id))}>
                              Remove
                            </Dropdown.Item>
                          </>
                        );
                      }}
                    </ListViewMenu>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {query.data.length === 0 && <ListView.Empty />}
          </ListView.Body>

          <ListView.Status total={query.data.length} label="Project" isLoading={query.status !== 'ready'} />
        </ListView>
      </Page.ListView>

      {action &&
        {
          edit: projectId && (
            <OpportunityProjectsForm
              opportunity={opportunity}
              availableAmount={stats.availableAmount}
              onSaved={handleSaved}
              onDeleted={handleDeleteConfirmation}
              onClose={handleClose}
            />
          ),
          view: projectId && <ViewOpportunityProject opportunityId={opportunity.id} onClose={handleClose} />,
        }[action]}

      {opportunity.permissions.manage && opportunityToProject && (
        <OpportunityToProjectForm
          opportunity={opportunity}
          onClose={() => setOpportunityToProject(false)}
          onSaved={handleCreated}
        />
      )}
    </>
  );
}
